import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { 
  Modal, 
  Button, 
  message,
  Row,
  Col,
  Pagination,
  Form,
  Input,
  Table,
  Tag,
  Select,
  Space,
  Empty,
  Spin,
  Alert,
  Card,
  Typography
} from 'antd';
import { SearchOutlined, PlusOutlined, HistoryOutlined, DatabaseOutlined, LinkOutlined, CheckCircleOutlined, WarningOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import './UserIpPermissions.css';
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';

const { Text } = Typography;

const UserIpPermissions = () => {
  const { hasPermission, isLoading: permissionLoading, userAccountType } = usePermissionCheck('canViewIPList');
  
  const [assignedIps, setAssignedIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalIps, setTotalIps] = useState(0); // 总IP数量
  const [searchTerm, setSearchTerm] = useState(''); // 实际搜索条件
  const [inputValue, setInputValue] = useState(''); // 搜索输入框的值

  const limit = 10; // 每页显示10条数据

  // 云平台UID绑定相关状态
  const [showBindUIDModal, setShowBindUIDModal] = useState(false);
  const [bindUIDForm, setBindUIDForm] = useState({ cloudPlatformUID: '' });
  const [bindUIDLoading, setBindUIDLoading] = useState(false);
  const [uidStatus, setUIDStatus] = useState({ isBound: false, cloudPlatformUID: null, bindTime: null });
  const [bindUIDMessage, setBindUIDMessage] = useState({ text: '', type: '', show: false });

  // IP权限申请相关状态
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({
    ipAddressInput: '',
    applicationReason: '',
    ipType: 'vps',
    serverId: ''
  });
  const [applyLoading, setApplyLoading] = useState(false);
  // 弹窗内消息状态
  const [modalMessage, setModalMessage] = useState({ text: '', type: '', show: false });

  // 申请记录相关状态
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsTotalPages, setRequestsTotalPages] = useState(1);
  const [requestsFilter, setRequestsFilter] = useState('');
  // 申请记录弹窗内消息状态
  const [requestsModalMessage, setRequestsModalMessage] = useState({ text: '', type: '', show: false }); // 状态筛选
  
  // IP详情模态框状态
  const [showIPDetailsModal, setShowIPDetailsModal] = useState(false);
  const [selectedIPRequest, setSelectedIPRequest] = useState(null);
  const [ipDetailPage, setIpDetailPage] = useState(1);
  const ipPageSize = 20;

  // 申请教程模态框状态
  const [showTutorialModal, setShowTutorialModal] = useState(false);

  // 获取当前用户分配的IP列表
  const fetchAssignedIps = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await API.get(`/api/user/assigned-ips?page=${page}&limit=${limit}&search=${search}`);
      
      // 适配新的API响应结构
      const ipRecords = response.data.assignedIps || [];
      setAssignedIps(ipRecords);
      setTotalIps(response.data.totalIps || 0);
      
      // 处理分页信息
      if (response.data.totalPages) {
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.currentPage);
      } else {
        // 如果没有分页信息，计算总页数
        setTotalPages(Math.ceil((response.data.totalIps || 0) / limit));
        setCurrentPage(page);
      }
      setLoading(false);
    } catch (error) {
      console.error('无法获取IP数据:', error);
      message.error('无法获取IP数据');
      setLoading(false);
    }
  };

  // 获取云平台UID绑定状态
  const fetchUIDStatus = async () => {
    try {
      const response = await API.get('/api/user/cloud-platform-uid-status');
      setUIDStatus(response.data);
    } catch (error) {
      console.error('获取UID绑定状态失败:', error);
    }
  };

  // 获取IP权限申请记录
  const fetchRequests = async (page = 1, status = '') => {
    setRequestsLoading(true);
    setRequestsModalMessage({ text: '', type: '', show: false }); // 清除之前的消息
    try {
      const response = await API.get(`/api/user/ip-permission-requests?page=${page}&limit=${limit}&status=${status}`);
      setRequests(response.data.requests);
      setRequestsTotalPages(response.data.totalPages);
      setRequestsPage(response.data.currentPage);
    } catch (error) {
      console.error('获取申请记录失败:', error);
      showRequestsModalMessage('获取申请记录失败', 'error');
    } finally {
      setRequestsLoading(false);
    }
  };

  // 显示弹窗内消息
  const showModalMessage = (text, type) => {
    setModalMessage({ text, type, show: true });
    setTimeout(() => {
      setModalMessage({ text: '', type: '', show: false });
    }, 3000);
  };

  // 显示申请记录弹窗内消息
  const showRequestsModalMessage = (text, type) => {
    setRequestsModalMessage({ text, type, show: true });
    setTimeout(() => {
      setRequestsModalMessage({ text: '', type: '', show: false });
    }, 3000);
  };

  // 显示UID绑定弹窗内消息
  const showBindUIDMessage = (text, type) => {
    setBindUIDMessage({ text, type, show: true });
    setTimeout(() => {
      setBindUIDMessage({ text: '', type: '', show: false });
    }, 3000);
  };

  // 提交UID绑定
  const handleBindUIDSubmit = async () => {
    if (!bindUIDForm.cloudPlatformUID.trim()) {
      showBindUIDMessage('请输入云平台账号ID', 'error');
      return;
    }

    setBindUIDLoading(true);
    setBindUIDMessage({ text: '', type: '', show: false });
    try {
      const response = await API.post('/api/user/bind-cloud-platform-uid', {
        cloudPlatformUID: bindUIDForm.cloudPlatformUID.trim()
      });
      
      showBindUIDMessage(response.data.message, 'success');
      // 更新UID状态
      setUIDStatus({
        isBound: true,
        cloudPlatformUID: response.data.cloudPlatformUID,
        bindTime: response.data.bindTime
      });
      
      // 延迟关闭弹窗
      setTimeout(() => {
        setShowBindUIDModal(false);
        setBindUIDForm({ cloudPlatformUID: '' });
        setBindUIDMessage({ text: '', type: '', show: false });
      }, 2000);
    } catch (error) {
      showBindUIDMessage(error.response?.data?.message || '绑定失败', 'error');
    } finally {
      setBindUIDLoading(false);
    }
  };

  // 提交IP权限申请
  const handleApplySubmit = async () => {
    // 检查是否已绑定UID
    if (!uidStatus.isBound) {
      showModalMessage('请先绑定云平台账号ID后再申请IP权限', 'error');
      return;
    }

    // 验证表单
    if (!applyForm.ipAddressInput.trim() || !applyForm.applicationReason.trim() || !applyForm.ipType) {
      showModalMessage('请填写完整的申请信息（IP地址、申请理由、IP类型）', 'error');
      return;
    }

    // 托管类型需要服务器ID
    if (applyForm.ipType === 'colocation' && !applyForm.serverId.trim()) {
      showModalMessage('托管类型的IP申请必须提供服务器ID', 'error');
      return;
    }

    setApplyLoading(true);
    setModalMessage({ text: '', type: '', show: false }); // 清除之前的消息
    try {
      const requestData = {
        ipAddressInput: applyForm.ipAddressInput,
        applicationReason: applyForm.applicationReason,
        ipType: applyForm.ipType
      };

      // 如果是托管类型，添加服务器ID
      if (applyForm.ipType === 'colocation') {
        requestData.serverId = applyForm.serverId.trim();
      }

      const response = await API.post('/api/user/apply-ip-permission', requestData);
      
      const { message, approvalType } = response.data;
      
      // 根据审批类型显示不同的消息
      let successMessage = message;
      if (approvalType === 'auto') {
        successMessage += ' (已自动审批通过)';
      } else if (approvalType === 'auto_rejected') {
        successMessage += ' (已自动驳回)';
      }
      
      showModalMessage(successMessage, approvalType === 'auto_rejected' ? 'warning' : 'success');
      
      // 延迟关闭弹窗，让用户看到成功消息
      setTimeout(() => {
        setShowApplyModal(false);
        setApplyForm({
          ipAddressInput: '',
          applicationReason: '',
          ipType: 'vps',
          serverId: ''
        });
        setModalMessage({ text: '', type: '', show: false });
        // 如果是自动审批通过，刷新IP列表
        if (approvalType === 'auto') {
          fetchAssignedIps(currentPage, searchTerm);
        }
      }, 3000);
    } catch (error) {
      showModalMessage(error.response?.data?.message || '提交申请失败', 'error');
    } finally {
      setApplyLoading(false);
    }
  };

  // 获取状态显示文本
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '待审核';
      case 'approved': return '已通过';
      case 'rejected': return '已拒绝';
      default: return '未知';
    }
  };

  // 格式化IP类型显示
  const formatIpType = (ipType) => {
    const typeMap = {
      'vps': { text: 'VPS', color: 'blue' },
      'physical': { text: '物理机', color: 'green' },
      'colocation': { text: '托管', color: 'orange' },
      'manual': { text: '人工', color: 'purple' }
    };
    return typeMap[ipType] || { text: ipType || 'VPS', color: 'default' };
  };

  // 显示IP详情
  const showIPDetails = (request) => {
    setSelectedIPRequest(request);
    setIpDetailPage(1); // 重置到第一页
    setShowIPDetailsModal(true);
  };

  useEffect(() => {
    fetchAssignedIps(currentPage, searchTerm); // 页面加载时获取数据
    if (userAccountType === 'main') {
      fetchUIDStatus(); // 获取UID绑定状态
    }
  }, [currentPage, searchTerm, userAccountType]);

  // 权限检查 - 移到所有hooks之后
  if (!hasPermission) {
    return (
      <PermissionDenied 
        isLoading={permissionLoading}
        permissionName="查看IP"
        userAccountType={userAccountType}
      />
    );
  }

  // 处理搜索输入变化 - 只更新输入框的值，不触发搜索
  const handleInputChange = (e) => {
    setInputValue(e.target.value); // 只更新输入框的值，不触发搜索
  };

  // 处理搜索按钮点击 - 触发实际搜索
  const handleSearch = () => {
    setSearchTerm(inputValue); // 设置搜索条件
    setCurrentPage(1); // 搜索时重置为第一页
    fetchAssignedIps(1, inputValue); // 执行搜索
  };

  // 处理回车键搜索
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div style={{ padding: 24 }}>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 页面标题 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, color: '#1890ff' }}>
            <DatabaseOutlined style={{ marginRight: 8 }} />
            我的IP权限
          </h2>
          {/* 只对main账号显示申请功能 */}
          {userAccountType === 'main' && (
            <Space>
              {!uidStatus.isBound ? (
                <Button 
                  type="primary" 
                  onClick={() => setShowBindUIDModal(true)}
                  icon={<LinkOutlined />}
                >
                  绑定云平台账号ID
                </Button>
              ) : (
                <Button 
                  type="primary" 
                  onClick={() => setShowApplyModal(true)}
                  icon={<PlusOutlined />}
                >
                  申请IP权限
                </Button>
              )}
              <Button 
                onClick={() => {
                  setShowRequestsModal(true);
                  fetchRequests(1, requestsFilter);
                }}
                icon={<HistoryOutlined />}
              >
                申请记录
              </Button>
              <Button 
                onClick={() => setShowTutorialModal(true)}
                icon={<QuestionCircleOutlined />}
              >
                申请教程
              </Button>
            </Space>
          )}
        </div>

        {/* UID绑定状态提示 */}
        {userAccountType === 'main' && (
          <Card size="small" className="uid-status-card">
            <div className="uid-status-content">
              <div className="uid-status-info">
                                  <Text strong>云平台账号ID绑定状态：</Text>
                  {uidStatus.isBound ? (
                    <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginLeft: 8 }}>
                      已绑定 ({uidStatus.cloudPlatformUID})
                    </Tag>
                  ) : (
                    <Tag color="orange" icon={<WarningOutlined />} style={{ marginLeft: 8 }}>
                      未绑定
                    </Tag>
                )}
                {uidStatus.isBound && uidStatus.bindTime && (
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    绑定时间：{new Date(uidStatus.bindTime).toLocaleString()}
                  </Text>
                )}
              </div>
              {!uidStatus.isBound && (
                <Button 
                  size="small" 
                  type="primary" 
                  onClick={() => setShowBindUIDModal(true)}
                >
                  立即绑定
                </Button>
              )}
            </div>
                         {!uidStatus.isBound && (
               <Alert
                 message="请先绑定云平台账号ID"
                 description="绑定账号ID后才能申请IP权限，每个账号只能绑定一次，请确保输入正确的账号ID。"
                 type="info"
                 showIcon
                 className="uid-binding-alert"
               />
             )}
          </Card>
        )}

        {/* 内容区域 */}
        <Card 
          title={
            <Space>
              <span>IP权限列表</span>
              <Tag color="blue">共 {totalIps} 个IP地址</Tag>
            </Space>
          }
          extra={
            /* 搜索区域 */
            <Space.Compact>
              <Input
                placeholder="搜索IP地址"
                value={inputValue}
                onChange={handleInputChange}
                onPressEnter={handleKeyPress}
                prefix={<SearchOutlined />}
                style={{ width: 280 }}
              />
              <Button 
                type="primary" 
                onClick={handleSearch}
                icon={<SearchOutlined />}
              >
                搜索
              </Button>
            </Space.Compact>
          }
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#666' }}>加载中...</div>
            </div>
          ) : (
            <>
              {assignedIps.length > 0 ? (
                <>
                  <Table
                    dataSource={assignedIps.map((ipRecord, index) => ({
                      key: index,
                      index: (currentPage - 1) * limit + index + 1,
                      ip: ipRecord.ip,
                      ipRecord: ipRecord
                    }))}
                    columns={[
                      {
                        title: '序号',
                        dataIndex: 'index',
                        key: 'index',
                        align: 'center',
                        width: 80,
                      },
                      {
                        title: 'IP地址',
                        dataIndex: 'ip',
                        key: 'ip',
                        render: (ip) => (
                          <Text 
                            copyable={{ text: ip }}
                            style={{ fontFamily: 'monospace' }}
                          >
                            {ip}
                          </Text>
                        ),
                      },
                      {
                        title: 'IP类型',
                        dataIndex: 'ipRecord',
                        key: 'ipType',
                        width: 150,
                        render: (ipRecord) => {
                          const typeConfig = formatIpType(ipRecord.ipType);
                          return (
                            <Tag color={typeConfig.color}>
                              {typeConfig.text}
                            </Tag>
                          );
                        },
                      },
                      // 只有main账号才显示共享用户列
                      ...(userAccountType === 'main' ? [{
                        title: '共享用户',
                        dataIndex: 'ipRecord',
                        key: 'sharedWith',
                        width: 200,
                        render: (ipRecord) => {
                          if (!ipRecord.sharedWith || ipRecord.sharedWith.length === 0) {
                            return <Text type="secondary">暂未共享</Text>;
                          }
                          return ipRecord.sharedWith.map(user => user.username).join(', ');
                        },
                      }] : []),
                      {
                        title: '创建时间',
                        dataIndex: 'ipRecord',
                        key: 'createdAt',
                        width: 180,
                        render: (ipRecord) => (
                          <span>{new Date(ipRecord.createdAt).toLocaleString('zh-CN')}</span>
                        ),
                      },
                    ]}
                    pagination={false}
                    bordered
                    size="middle"
                  />

                  {/* 分页控件 */}
                  {totalPages > 1 && (
                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                      <Pagination
                        current={currentPage}
                        total={totalIps}
                        pageSize={limit}
                        onChange={(page) => {
                          setCurrentPage(page);
                          fetchAssignedIps(page, searchTerm);
                        }}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Empty
                  description={
                    <div>
                      <div>当前没有分配的IP地址</div>
                      <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>
                        {userAccountType === 'main' 
                          ? '您可以点击"申请IP权限"按钮申请新的IP访问权限' 
                          : '请联系管理员分配IP访问权限'
                        }
                      </div>
                    </div>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                                    {userAccountType === 'main' && (
                    <Space>
                      {uidStatus.isBound ? (
                        <Button 
                          type="primary" 
                          onClick={() => setShowApplyModal(true)}
                          icon={<PlusOutlined />}
                        >
                          申请IP权限
                        </Button>
                      ) : (
                        <Button 
                          type="primary" 
                          onClick={() => setShowBindUIDModal(true)}
                          icon={<LinkOutlined />}
                        >
                          绑定云平台账号ID
                        </Button>
                      )}
                      <Button 
                        onClick={() => setShowTutorialModal(true)}
                        icon={<QuestionCircleOutlined />}
                      >
                        查看教程
                      </Button>
                    </Space>
                  )}
                </Empty>
              )}
            </>
          )}
        </Card>

        {/* 云平台UID绑定模态框 */}
        <Modal
          title={
            <Space>
              <LinkOutlined />
              <span>绑定云平台账号ID</span>
            </Space>
          }
          open={showBindUIDModal}
          onCancel={() => {
            setShowBindUIDModal(false);
            setBindUIDMessage({ text: '', type: '', show: false });
            setBindUIDForm({ cloudPlatformUID: '' });
          }}
          footer={[
            <Button key="cancel" onClick={() => {
              setShowBindUIDModal(false);
              setBindUIDMessage({ text: '', type: '', show: false });
              setBindUIDForm({ cloudPlatformUID: '' });
            }}>
              取消
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              onClick={handleBindUIDSubmit}
              loading={bindUIDLoading}
            >
              确认绑定
            </Button>
          ]}
          width={600}
          className="uid-bind-modal"
        >
          {/* 弹窗内消息提示 */}
          {bindUIDMessage.show && (
            <Alert
              message={bindUIDMessage.text}
              type={bindUIDMessage.type === 'error' ? 'error' : 'success'}
              showIcon
              style={{ marginBottom: 16 }}
              closable
              onClose={() => setBindUIDMessage({ text: '', type: '', show: false })}
            />
          )}

          <Alert
            message="重要提示"
            description="每个账号只能绑定一次云平台账号ID，绑定后无法修改，请确保输入正确的账号ID。系统会验证该账号ID在权限数据库中是否存在。"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form layout="vertical">
            <Form.Item 
              label="云平台UID" 
              required
              help="请输入您在云平台的账号ID，系统会自动验证该账号ID的有效性"
            >
              <Input
                placeholder="请输入云平台账号ID"
                value={bindUIDForm.cloudPlatformUID}
                onChange={(e) => setBindUIDForm({
                  cloudPlatformUID: e.target.value
                })}
                size="large"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* IP权限申请模态框 - Ant Design */}
        <Modal
          title="申请IP权限"
          open={showApplyModal}
          onCancel={() => {
            setShowApplyModal(false);
            setModalMessage({ text: '', type: '', show: false });
          }}
          footer={[
            <Button key="cancel" onClick={() => {
              setShowApplyModal(false);
              setModalMessage({ text: '', type: '', show: false });
            }}>
              取消
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              onClick={handleApplySubmit}
              loading={applyLoading}
              disabled={!uidStatus.isBound}
            >
              提交申请
            </Button>
          ]}
          width={700}
        >
          {/* 弹窗内消息提示 */}
          {modalMessage.show && (
            <Alert
              message={modalMessage.text}
              type={modalMessage.type === 'error' ? 'error' : 'success'}
              showIcon
              style={{ marginBottom: 16 }}
              closable
              onClose={() => setModalMessage({ text: '', type: '', show: false })}
            />
          )}
          
          <Form layout="vertical">
            <Form.Item 
              label="IP地址" 
              required
              help="支持单个IP、IP段（1.1.1.1-1.1.1.10）、CIDR（1.1.1.0/24）等格式，用逗号、换行或空格分隔"
            >
              <Input.TextArea
                rows={4}
                placeholder="支持多种IP输入格式，每行一个：&#10;• 单个IP：192.168.1.1&#10;• IP段：192.168.1.1-192.168.1.10&#10;• CIDR：192.168.1.0/24"
                value={applyForm.ipAddressInput}
                onChange={(e) => setApplyForm({
                  ...applyForm,
                  ipAddressInput: e.target.value
                })}
              />
            </Form.Item>

            <Form.Item 
              label="IP类型" 
              required
              help="请选择申请IP的类型"
            >
              <Select
                value={applyForm.ipType}
                onChange={(value) => setApplyForm({
                  ...applyForm,
                  ipType: value,
                  serverId: value !== 'colocation' ? '' : applyForm.serverId // 非托管类型清空服务器ID
                })}
                style={{ width: '100%' }}
              >
                <Select.Option value="vps">VPS</Select.Option>
                <Select.Option value="physical">物理机</Select.Option>
                <Select.Option value="colocation">托管</Select.Option>
              </Select>
            </Form.Item>

            {applyForm.ipType === 'colocation' && (
              <Form.Item 
                label="服务器ID" 
                required
                help="托管类型的IP申请必须提供服务器ID，您可以在DCIM系统中查看服务器ID"
              >
                <Input
                  placeholder="请输入服务器ID（可在DCIM系统中查看）"
                  value={applyForm.serverId}
                  onChange={(e) => setApplyForm({
                    ...applyForm,
                    serverId: e.target.value
                  })}
                />
              </Form.Item>
            )}
            
            <Form.Item label="已绑定云平台账号ID">
              <div style={{ 
                padding: '8px 12px', 
                border: '1px solid #d9d9d9', 
                borderRadius: 6, 
                backgroundColor: '#f6f8fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Text strong style={{ color: '#1890ff' }}>
                  {uidStatus.cloudPlatformUID || '未绑定'}
                </Text>
                {uidStatus.isBound && (
                  <Tag color="green" size="small">已绑定</Tag>
                )}
              </div>
            </Form.Item>
            
            <Form.Item 
              label="申请理由" 
              required
              help="请详细说明申请此IP权限的用途和理由，有助于加快审核速度"
            >
              <Input.TextArea
                rows={4}
                placeholder="例如：需要监测此IP的流量..."
                value={applyForm.applicationReason}
                onChange={(e) => setApplyForm({
                  ...applyForm,
                  applicationReason: e.target.value
                })}
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* 申请记录模态框 - Ant Design */}
        <Modal
          title={
            <Space>
              我的申请记录
              <Tag color="default">{requests.length}</Tag>
            </Space>
          }
          open={showRequestsModal}
          onCancel={() => {
            setShowRequestsModal(false);
            setRequestsModalMessage({ text: '', type: '', show: false });
          }}
          footer={[
            <Button key="close" onClick={() => {
              setShowRequestsModal(false);
              setRequestsModalMessage({ text: '', type: '', show: false });
            }}>
              关闭
            </Button>
          ]}
          width={1200}
        >
          {/* 弹窗内消息提示 */}
          {requestsModalMessage.show && (
            <Alert
              message={requestsModalMessage.text}
              type={requestsModalMessage.type === 'error' ? 'error' : 'success'}
              showIcon
              style={{ marginBottom: 16 }}
              closable
              onClose={() => setRequestsModalMessage({ text: '', type: '', show: false })}
            />
          )}

          {/* 筛选工具栏 */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 16,
            padding: '12px 16px',
            background: '#fafafa',
            borderRadius: 6
          }}>
            <Space>
              <span>状态筛选：</span>
              <Select
                value={requestsFilter}
                onChange={(value) => {
                  setRequestsFilter(value);
                  fetchRequests(1, value);
                }}
                style={{ width: 140 }}
                size="small"
              >
                <Select.Option value="">全部状态</Select.Option>
                <Select.Option value="pending">待审核</Select.Option>
                <Select.Option value="approved">已通过</Select.Option>
                <Select.Option value="rejected">已拒绝</Select.Option>
              </Select>
            </Space>
            <span style={{ color: '#666', fontSize: 12 }}>
              最近更新：{requests.length > 0 ? new Date(requests[0]?.updatedAt || requests[0]?.createdAt).toLocaleString() : '-'}
            </span>
          </div>

          {requestsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#666' }}>正在加载申请记录...</div>
            </div>
          ) : requests.length > 0 ? (
            <>
              <Table
                dataSource={requests.map((request, index) => ({
                  ...request,
                  key: request.id,
                  index: index + 1
                }))}
                columns={[
                  {
                    title: '申请IP',
                    dataIndex: 'ipCount',
                    key: 'ipCount',
                    render: (ipCount, record, index) => (
                      <Space>
                        <Tag color="default">#{index + 1}</Tag>
                        <Tag 
                          color="blue" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => showIPDetails(record)}
                        >
                          {ipCount || 0} 个IP
                        </Tag>
                      </Space>
                    )
                  },
                  {
                    title: 'IP类型',
                    dataIndex: 'ipType',
                    key: 'ipType',
                    width: 100,
                    render: (ipType) => {
                      const typeConfig = formatIpType(ipType);
                      return <Tag color={typeConfig.color}>{typeConfig.text}</Tag>;
                    }
                  },
                  {
                    title: '申请理由',
                    dataIndex: 'applicationReason',
                    key: 'applicationReason',
                    ellipsis: { showTitle: true },
                    width: 200
                  },
                  {
                    title: '状态',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status) => (
                      <Tag 
                        color={status === 'pending' ? 'orange' : status === 'approved' ? 'green' : 'red'}
                      >
                        {getStatusText(status)}
                      </Tag>
                    )
                  },
                  {
                    title: '审核意见',
                    dataIndex: 'reviewComment',
                    key: 'reviewComment',
                    ellipsis: { showTitle: true },
                    width: 200,
                    render: (comment) => comment || '-'
                  },
                  {
                    title: '申请时间',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    render: (time) => (
                      <div>
                        <div>{new Date(time).toLocaleDateString()}</div>
                        <div style={{ fontSize: 11, color: '#999' }}>
                          {new Date(time).toLocaleTimeString()}
                        </div>
                      </div>
                    )
                  },
                  {
                    title: '审核时间',
                    dataIndex: 'reviewedAt',
                    key: 'reviewedAt',
                    render: (time) => time ? (
                      <div>
                        <div>{new Date(time).toLocaleDateString()}</div>
                        <div style={{ fontSize: 11, color: '#999' }}>
                          {new Date(time).toLocaleTimeString()}
                        </div>
                      </div>
                    ) : '-'
                  }
                ]}
                pagination={requestsTotalPages > 1 ? {
                  current: requestsPage,
                  total: requestsTotalPages * 10,
                  pageSize: 10,
                  onChange: (page) => fetchRequests(page, requestsFilter),
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`
                } : false}
                size="small"
              />
            </>
          ) : (
            <Empty
              description={
                <div>
                  <div>暂无申请记录</div>
                  <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>
                    您还没有提交过IP权限申请
                  </div>
                </div>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              {uidStatus.isBound ? (
                <Button 
                  type="primary" 
                  onClick={() => {
                    setShowRequestsModal(false);
                    setRequestsModalMessage({ text: '', type: '', show: false });
                    setShowApplyModal(true);
                  }}
                >
                  立即申请IP权限
                </Button>
              ) : (
                                  <Button 
                    type="primary" 
                    icon={<LinkOutlined />}
                    onClick={() => {
                      setShowRequestsModal(false);
                      setRequestsModalMessage({ text: '', type: '', show: false });
                      setShowBindUIDModal(true);
                    }}
                  >
                    绑定云平台账号ID
                  </Button>
              )}
            </Empty>
          )}
          
          <div style={{ 
            marginTop: 16, 
            textAlign: 'center', 
            color: '#999', 
            fontSize: 12,
            borderTop: '1px solid #f0f0f0',
            paddingTop: 12
          }}>
            申请记录会实时更新，请关注审核状态变化
          </div>
        </Modal>

        {/* IP详情模态框 - 简化版本 */}
        <Modal
          title="IP详情"
          open={showIPDetailsModal}
          onCancel={() => setShowIPDetailsModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowIPDetailsModal(false)}>
              关闭
            </Button>
          ]}
          width={800}
          zIndex={2000}
          className="ip-details-modal"
        >
          {selectedIPRequest && (
            <div>
              {/* 基本信息 */}
              <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 6 }}>
                <div>申请时间：{new Date(selectedIPRequest.createdAt).toLocaleString()}</div>
                <div style={{ marginTop: 8 }}>IP数量：{selectedIPRequest.ipCount} 个</div>
                <div style={{ marginTop: 8 }}>
                  IP类型：
                  {(() => {
                    const typeConfig = formatIpType(selectedIPRequest.ipType);
                    return <Tag color={typeConfig.color}>{typeConfig.text}</Tag>;
                  })()}
                </div>
                {selectedIPRequest.ipType === 'colocation' && selectedIPRequest.serverId && (
                  <div style={{ marginTop: 8 }}>
                    服务器ID：<Text code>{selectedIPRequest.serverId}</Text>
                  </div>
                )}
              </div>

              {/* IP地址列表 */}
              <div style={{ marginBottom: 16 }}>
                <h4>IP地址</h4>
                {selectedIPRequest.ipAddresses && selectedIPRequest.ipAddresses.length > 0 ? (
                  <>
                    <Row gutter={[8, 8]}>
                      {selectedIPRequest.ipAddresses
                        .slice((ipDetailPage - 1) * ipPageSize, ipDetailPage * ipPageSize)
                        .map((ip, index) => {
                          const realIndex = (ipDetailPage - 1) * ipPageSize + index;
                          return (
                            <Col key={realIndex} span={6}>
                              <div 
                                style={{ 
                                  padding: '8px 12px',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: 4,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  fontSize: 13
                                }}
                                onClick={() => {
                                  navigator.clipboard.writeText(ip);
                                  message.success('已复制');
                                }}
                              >
                                <div style={{ color: '#666', fontSize: 10 }}>#{realIndex + 1}</div>
                                <div style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{ip}</div>
                              </div>
                            </Col>
                          );
                        })}
                    </Row>
                    {selectedIPRequest.ipAddresses.length > ipPageSize && (
                      <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Pagination
                          current={ipDetailPage}
                          total={selectedIPRequest.ipAddresses.length}
                          pageSize={ipPageSize}
                          onChange={setIpDetailPage}
                          showSizeChanger={false}
                          showQuickJumper
                          showTotal={(total, range) => `${range[0]}-${range[1]} / 共 ${total} 个IP`}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: '#999' }}>暂无IP地址</div>
                )}
              </div>

              {/* 原始输入 */}
              <div>
                <h4>原始输入</h4>
                <div 
                  style={{
                    background: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    padding: 12,
                    fontFamily: 'monospace',
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(selectedIPRequest.ipAddressInput);
                    message.success('已复制');
                  }}
                >
                  {selectedIPRequest.ipAddressInput}
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* 申请教程模态框 */}
        <Modal
          title={
            <Space>
              <QuestionCircleOutlined />
              <span>IP权限申请教程</span>
            </Space>
          }
          open={showTutorialModal}
          onCancel={() => setShowTutorialModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowTutorialModal(false)}>
              我知道了
            </Button>,
            <Button 
              key="apply" 
              type="primary" 
              onClick={() => {
                setShowTutorialModal(false);
                if (uidStatus.isBound) {
                  setShowApplyModal(true);
                } else {
                  setShowBindUIDModal(true);
                }
              }}
              disabled={userAccountType !== 'main'}
            >
              立即申请
            </Button>
          ]}
          width={800}
          centered
          className="tutorial-modal"
          bodyStyle={{ 
            maxHeight: '70vh', 
            overflowY: 'auto',
            padding: '20px 24px'
          }}
        >
          <div style={{ 
            lineHeight: '1.6',
            paddingRight: '8px' // 为滚动条留出空间
          }}>
            {/* 申请前准备 */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ color: '#1890ff', marginBottom: 12, fontSize: '16px' }}>📋 申请前准备</h3>
              <div style={{ padding: '16px', backgroundColor: '#f6f8fa', borderRadius: '6px', marginBottom: 12 }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div><strong>1. 绑定云平台账号ID</strong></div>
                  <div style={{ paddingLeft: 16, color: '#666' }}>
                    • 每个账号只能绑定一次，请确保输入正确<br/>
                    • 系统会验证账号ID的有效性<br/>
                    • 绑定后才能申请IP权限
                  </div>
                </Space>
              </div>
            </div>

            {/* IP格式说明 */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ color: '#1890ff', marginBottom: 12, fontSize: '16px' }}>🌐 支持的IP格式</h3>
              <div style={{ padding: '16px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div><strong>单个IP地址：</strong></div>
                  <div style={{ 
                    fontFamily: 'monospace', 
                    backgroundColor: '#fff', 
                    padding: '8px', 
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}>
                    192.168.1.100
                  </div>
                  
                  <div style={{ marginTop: 12 }}><strong>IP范围：</strong></div>
                  <div style={{ 
                    fontFamily: 'monospace', 
                    backgroundColor: '#fff', 
                    padding: '8px', 
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}>
                    192.168.1.1-192.168.1.10
                  </div>
                  
                  <div style={{ marginTop: 12 }}><strong>CIDR网段：</strong></div>
                  <div style={{ 
                    fontFamily: 'monospace', 
                    backgroundColor: '#fff', 
                    padding: '8px', 
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}>
                    192.168.1.0/24
                  </div>
                  
                  <div style={{ marginTop: 12 }}><strong>多个IP（用逗号、换行或空格分隔）：</strong></div>
                  <div style={{ 
                    fontFamily: 'monospace', 
                    backgroundColor: '#fff', 
                    padding: '8px', 
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}>
                    192.168.1.1, 192.168.1.2<br/>
                    10.0.0.0/24<br/>
                    172.16.1.1-172.16.1.50
                  </div>
                </Space>
              </div>
            </div>

            {/* IP类型说明 */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ color: '#1890ff', marginBottom: 12, fontSize: '16px' }}>🏷️ IP类型说明</h3>
              <div style={{ padding: '16px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Tag color="blue" style={{ marginRight: 12, minWidth: 50, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>VPS</Tag>
                    <span>虚拟专用服务器IP地址</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Tag color="green" style={{ marginRight: 12, minWidth: 50, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>物理机</Tag>
                    <span>物理服务器IP地址</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Tag color="orange" style={{ marginRight: 12, minWidth: 50, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>托管</Tag>
                    <span>托管服务器IP地址（需要提供服务器ID，可在DCIM系统中查看）</span>
                  </div>
                </Space>
              </div>
            </div>

            {/* 申请流程 */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ color: '#1890ff', marginBottom: 12, fontSize: '16px' }}>📝 申请流程</h3>
              <div style={{ padding: '16px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div><strong>第1步：</strong> 选择IP类型（VPS、物理机、托管）</div>
                  <div><strong>第2步：</strong> 输入需要申请的IP地址</div>
                  <div><strong>第3步：</strong> 填写详细的申请理由</div>
                  <div><strong>第4步：</strong> 提交申请等待审核</div>
                  <div><strong>第5步：</strong> 审核通过后即可使用IP权限</div>
                </Space>
              </div>
            </div>

            {/* 审核说明 */}
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ color: '#1890ff', marginBottom: 12, fontSize: '16px' }}>⏰ 审核说明</h3>
              <Alert
                message="审核时间"
                description={
                  <div>
                    <div>• <strong>自动审批：</strong>符合预设规则的申请会立即通过</div>
                    <div>• <strong>人工审批：</strong>其他申请通常在1-3个工作日内完成审核</div>
                    <div>• <strong>申请理由：</strong>详细的申请理由有助于加快审核速度</div>
                  </div>
                }
                type="info"
                showIcon
              />
            </div>

            {/* 注意事项 */}
            <div>
              <h3 style={{ color: '#ff4d4f', marginBottom: 12, fontSize: '16px' }}>⚠️ 注意事项</h3>
              <Alert
                message="重要提醒"
                description={
                  <div>
                    <div>• 请确保申请的IP地址格式正确</div>
                    <div>• 申请理由必须真实详细，虚假申请可能被拒绝</div>
                    <div>• 托管类型IP必须提供正确的服务器ID（可在DCIM系统中查看）</div>
                    <div>• 申请通过后请合理使用IP权限，避免滥用</div>
                  </div>
                }
                type="warning"
                showIcon
              />
            </div>
          </div>
        </Modal>
      </Space>
    </div>
  );
};

export default UserIpPermissions;
